import {
  buildAdminPath,
  buildJoinPath,
  buildModeratorPath,
  buildProjectorPath,
  normalizeJoinCode,
} from "./join.ts";

export type SessionHandoffSurface = "participant" | "admin" | "moderator" | "projector";

export type SessionHandoffRoute =
  | { surface: SessionHandoffSurface; currentJoinCode: string | null }
  | { surface: "excluded"; currentJoinCode: null };

export type SessionHandoffTarget = {
  revision: number;
  joinCode: string;
};

export type SessionHandoffAction =
  | { type: "none" }
  | { type: "record"; revision: number }
  | { type: "navigate"; revision: number; path: string };

const SCOPED_ROUTES: Array<{
  pattern: RegExp;
  surface: SessionHandoffSurface;
}> = [
  { pattern: /^\/join\/([^/]+)(?:\/.*)?$/, surface: "participant" },
  { pattern: /^\/admin\/session\/([^/]+)(?:\/.*)?$/, surface: "admin" },
  { pattern: /^\/moderator\/session\/([^/]+)\/?$/, surface: "moderator" },
  { pattern: /^\/projector\/session\/([^/]+)\/?$/, surface: "projector" },
];

export function classifySessionHandoffRoute(pathname: string): SessionHandoffRoute {
  const normalizedPath = normalizePathname(pathname);

  for (const route of SCOPED_ROUTES) {
    const match = normalizedPath.match(route.pattern);
    if (!match) continue;
    const currentJoinCode = normalizeJoinCode(match[1]);
    if (currentJoinCode.length !== 6) return excludedRoute();
    return { surface: route.surface, currentJoinCode };
  }

  if (normalizedPath === "/participate" || normalizedPath === "/join") {
    return { surface: "participant", currentJoinCode: null };
  }
  if (normalizedPath === "/" || normalizedPath === "/admin") {
    return { surface: "admin", currentJoinCode: null };
  }
  if (normalizedPath === "/moderator") {
    return { surface: "moderator", currentJoinCode: null };
  }
  if (normalizedPath === "/projector") {
    return { surface: "projector", currentJoinCode: null };
  }
  return excludedRoute();
}

export function resolveSessionHandoffAction({
  route,
  target,
  seenRevision,
}: {
  route: SessionHandoffRoute;
  target: SessionHandoffTarget | null;
  seenRevision: number | null;
}): SessionHandoffAction {
  if (route.surface === "excluded") return { type: "none" };

  if (seenRevision === null) {
    return { type: "record", revision: target?.revision ?? 0 };
  }
  if (!target || target.revision <= seenRevision) return { type: "none" };

  const targetJoinCode = normalizeJoinCode(target.joinCode);
  if (targetJoinCode.length !== 6 || route.currentJoinCode === targetJoinCode) {
    return { type: "record", revision: target.revision };
  }

  return {
    type: "navigate",
    revision: target.revision,
    path: targetPath(route.surface, targetJoinCode),
  };
}

function targetPath(surface: SessionHandoffSurface, joinCode: string): string {
  if (surface === "participant") return buildJoinPath(joinCode);
  if (surface === "admin") return buildAdminPath(joinCode);
  if (surface === "moderator") return buildModeratorPath(joinCode);
  return buildProjectorPath(joinCode);
}

function normalizePathname(pathname: string): string {
  if (!pathname) return "/";
  const withoutTrailingSlash = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return withoutTrailingSlash || "/";
}

function excludedRoute(): SessionHandoffRoute {
  return { surface: "excluded", currentJoinCode: null };
}
